import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageContainer, PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Plus, X, BookOpen, ListTree, FileBarChart, Printer, Trash2,
  Pencil, RotateCcw, CheckCircle2, XCircle, AlertCircle, Search,
  TrendingUp, Wallet,
} from "lucide-react";
import { useConfirm } from "@/components/ConfirmDialog";
import { useAuth } from "@/contexts/AuthContext";

const money = (n) =>
  `RD$${Number(n || 0).toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const TYPE_META = {
  activo:     { label: "Activo",     color: "text-emerald-700 dark:text-emerald-400" },
  pasivo:     { label: "Pasivo",     color: "text-rose-700 dark:text-rose-400" },
  patrimonio: { label: "Patrimonio", color: "text-indigo-700 dark:text-indigo-400" },
  ingreso:    { label: "Ingreso",    color: "text-cyan-700 dark:text-cyan-400" },
  gasto:      { label: "Gasto",      color: "text-amber-700 dark:text-amber-400" },
  costo:      { label: "Costo",      color: "text-orange-700 dark:text-orange-400" },
};

// ─────────────────────────────── CATÁLOGO ───────────────────────────────
export function CoaTab() {
  const [accounts, setAccounts] = useState([]);
  const [q, setQ] = useState("");
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const { user } = useAuth();
  const canEdit = ["admin", "contador"].includes((user?.role || "").toLowerCase());
  const confirm = useConfirm();

  const load = async () => {
    try {
      const { data } = await api.get("/accounting/coa");
      setAccounts(data);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return accounts;
    const s = q.toLowerCase();
    return accounts.filter((a) =>
      a.code.toLowerCase().includes(s) || a.name.toLowerCase().includes(s));
  }, [accounts, q]);

  const remove = async (a) => {
    const ok = await confirm({
      title: `Eliminar "${a.code} — ${a.name}"?`,
      description: "Solo se puede eliminar si no tiene movimientos ni sub-cuentas.",
      confirmLabel: "Eliminar", destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/accounting/coa/${a.id}`);
      toast.success("Cuenta eliminada");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-3">
        <div className="flex items-center gap-2 flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por código o nombre…" className="h-8 max-w-md" data-testid="coa-search" />
          {q && <Button size="sm" variant="ghost" onClick={() => setQ("")}><X className="w-4 h-4" /></Button>}
        </div>
        <span className="text-xs text-muted-foreground self-center ml-auto mr-2">{filtered.length} cuentas</span>
        {canEdit && (
          <Button onClick={() => { setEditing(null); setOpenForm(true); }} className="bg-primary font-bold" data-testid="new-account-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Nueva cuenta
          </Button>
        )}
      </div>
      <div className="bg-card border border-border rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="overline border-b border-border">
            <tr>
              <th className="text-left p-3">Código</th>
              <th className="text-left p-3">Nombre</th>
              <th className="text-left p-3">Tipo</th>
              <th className="text-left p-3">Naturaleza</th>
              <th className="text-center p-3">Header</th>
              <th className="text-center p-3">Contra</th>
              <th className="text-center p-3">Sistema</th>
              <th className="text-center p-3">Activa</th>
              {canEdit && <th className="text-right p-3">Acciones</th>}
            </tr>
          </thead>
          <tbody>
            {filtered.map((a) => (
              <tr key={a.id}
                  className={`border-b border-border/60 hover:bg-accent ${a.is_header ? "font-bold" : ""} ${!a.active ? "opacity-50" : ""}`}
                  data-testid={`account-row-${a.code}`}>
                <td className="p-3 data-cell font-bold" style={{ paddingLeft: `${(a.code.length - 1) * 12 + 12}px` }}>{a.code}</td>
                <td className="p-3">{a.name}</td>
                <td className={`p-3 text-xs uppercase ${TYPE_META[a.type]?.color || ""}`}>{TYPE_META[a.type]?.label || a.type}</td>
                <td className="p-3 text-xs data-cell">{a.normal_balance === "debit" ? "Deudora" : "Acreedora"}</td>
                <td className="p-3 text-center">{a.is_header ? "✓" : ""}</td>
                <td className="p-3 text-center">{a.is_contra ? "✓" : ""}</td>
                <td className="p-3 text-center">{a.system && <span className="text-xs px-1.5 py-0.5 bg-primary/10 text-primary rounded">SIST</span>}</td>
                <td className="p-3 text-center">{a.active ? <CheckCircle2 className="w-4 h-4 text-emerald-600 inline" /> : <XCircle className="w-4 h-4 text-destructive inline" />}</td>
                {canEdit && (
                  <td className="p-3 text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" onClick={() => { setEditing(a); setOpenForm(true); }} data-testid={`edit-${a.code}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    {!a.system && (
                      <Button size="sm" variant="ghost" onClick={() => remove(a)} data-testid={`delete-${a.code}`}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {canEdit && (
        <AccountFormDialog
          open={openForm} onOpenChange={setOpenForm}
          editing={editing} accounts={accounts}
          onSaved={() => { setOpenForm(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function AccountFormDialog({ open, onOpenChange, editing, accounts, onSaved }) {
  const [form, setForm] = useState(defaultAccount());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(editing ? { ...editing } : defaultAccount());
  }, [open, editing]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) await api.put(`/accounting/coa/${editing.id}`, form);
      else         await api.post("/accounting/coa", form);
      toast.success("Guardado");
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{editing ? `Editar ${editing.code}` : "Nueva cuenta"}</DialogTitle>
          <DialogDescription>Las cuentas &lsquo;header&rsquo; agrupan y no aceptan asientos; las hoja sí.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-2 gap-3">
          <div><Label>Código *</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} required className="mt-1 data-cell" disabled={!!(editing && editing.system)} data-testid="form-code" /></div>
          <div><Label>Nombre *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1" data-testid="form-name" /></div>
          <div>
            <Label>Tipo</Label>
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })} disabled={!!(editing && editing.system)}>
              <SelectTrigger className="mt-1" data-testid="form-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(TYPE_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Naturaleza (saldo normal)</Label>
            <Select value={form.normal_balance} onValueChange={(v) => setForm({ ...form, normal_balance: v })} disabled={!!(editing && editing.system)}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="debit">Deudora</SelectItem>
                <SelectItem value="credit">Acreedora</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Cuenta padre (opcional)</Label>
            <Select value={form.parent_code || "__none__"} onValueChange={(v) => setForm({ ...form, parent_code: v === "__none__" ? null : v })}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— (sin padre / raíz)</SelectItem>
                {accounts.filter((a) => a.is_header && a.code !== form.code).map((a) => (
                  <SelectItem key={a.code} value={a.code}>{a.code} · {a.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-4 pt-4">
            <label className="flex items-center gap-2"><Switch checked={form.is_header} onCheckedChange={(v) => setForm({ ...form, is_header: v })} /> Header</label>
            <label className="flex items-center gap-2"><Switch checked={form.is_contra} onCheckedChange={(v) => setForm({ ...form, is_contra: v })} /> Contra</label>
            <label className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /> Activa</label>
          </div>
          <div className="col-span-2">
            <Label>Descripción</Label>
            <Textarea value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="mt-1" />
          </div>
          <DialogFooter className="col-span-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={saving} className="bg-primary font-bold" data-testid="form-save">Guardar</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function defaultAccount() {
  return { code: "", name: "", type: "gasto", normal_balance: "debit", is_header: false, is_contra: false, parent_code: null, active: true, description: "" };
}

// ─────────────────────────────── JOURNAL ───────────────────────────────
export function JournalTab() {
  const [entries, setEntries] = useState([]);
  const [openForm, setOpenForm] = useState(false);
  const [openDetail, setOpenDetail] = useState(null);
  const { user } = useAuth();
  const canEdit = ["admin", "contador"].includes((user?.role || "").toLowerCase());
  const confirm = useConfirm();

  const load = async () => {
    try { const { data } = await api.get("/accounting/journal"); setEntries(data); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, []);

  const doReverse = async (e) => {
    const ok = await confirm({
      title: `Anular asiento ${e.number}?`,
      description: "Se creará un asiento inverso automáticamente. Ninguno se elimina.",
      confirmLabel: "Anular", destructive: true,
    });
    if (!ok) return;
    try {
      await api.post(`/accounting/journal/${e.id}/reverse`);
      toast.success("Asiento anulado");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <span className="text-xs text-muted-foreground ml-auto mr-2">{entries.length} asientos</span>
        {canEdit && (
          <Button onClick={() => setOpenForm(true)} className="bg-primary font-bold" data-testid="new-journal-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Nuevo asiento
          </Button>
        )}
      </div>
      <div className="bg-card border border-border rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="overline border-b border-border">
            <tr>
              <th className="text-left p-3">No.</th>
              <th className="text-left p-3">Fecha</th>
              <th className="text-left p-3">Descripción</th>
              <th className="text-left p-3">Referencia</th>
              <th className="text-left p-3">Origen</th>
              <th className="text-right p-3">Débitos</th>
              <th className="text-right p-3">Créditos</th>
              <th className="text-center p-3">Estado</th>
              <th className="text-right p-3">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => (
              <tr key={e.id}
                  className={`border-b border-border/60 hover:bg-accent ${e.status === "reversed" || e.reverses ? "opacity-50" : ""}`}
                  data-testid={`journal-row-${e.number}`}>
                <td className="p-3 data-cell font-bold">{e.number}</td>
                <td className="p-3 data-cell">{(e.date || "").slice(0, 10)}</td>
                <td className="p-3">{e.description}</td>
                <td className="p-3 data-cell text-xs">{e.reference || "—"}</td>
                <td className="p-3 text-xs uppercase">{e.source_type}</td>
                <td className="p-3 text-right data-cell font-bold">{money(e.total_debit)}</td>
                <td className="p-3 text-right data-cell font-bold">{money(e.total_credit)}</td>
                <td className="p-3 text-center">
                  {e.status === "reversed" ? <span className="text-xs px-2 py-0.5 rounded bg-destructive/10 text-destructive">ANULADO</span>
                   : e.reverses ? <span className="text-xs px-2 py-0.5 rounded bg-muted">Reversa</span>
                   : <span className="text-xs px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-700 dark:text-emerald-400">Activo</span>}
                </td>
                <td className="p-3 text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" onClick={() => setOpenDetail(e)} data-testid={`view-${e.number}`}>Ver</Button>
                  {canEdit && e.status !== "reversed" && !e.reverses && (
                    <Button size="sm" variant="ghost" onClick={() => doReverse(e)} data-testid={`reverse-${e.number}`}>
                      <RotateCcw className="w-4 h-4 text-destructive" />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {entries.length === 0 && (
              <tr><td colSpan={9} className="p-8 text-center text-muted-foreground">Sin asientos aún.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {canEdit && <JournalFormDialog open={openForm} onOpenChange={setOpenForm} onSaved={() => { setOpenForm(false); load(); }} />}
      <JournalDetailDialog entry={openDetail} onClose={() => setOpenDetail(null)} />
    </div>
  );
}

function JournalFormDialog({ open, onOpenChange, onSaved }) {
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), description: "", reference: "", lines: [emptyLine(), emptyLine()] });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      api.get("/accounting/coa", { params: { active_only: true } }).then(({ data }) => setAccounts(data.filter((a) => !a.is_header)));
      setForm({ date: new Date().toISOString().slice(0, 10), description: "", reference: "", lines: [emptyLine(), emptyLine()] });
    }
  }, [open]);

  const totals = useMemo(() => {
    const td = form.lines.reduce((s, l) => s + Number(l.debit || 0), 0);
    const tc = form.lines.reduce((s, l) => s + Number(l.credit || 0), 0);
    return { td, tc, balanced: Math.abs(td - tc) < 0.01 && td > 0 };
  }, [form.lines]);

  const updateLine = (i, field, value) => {
    setForm((f) => ({
      ...f,
      lines: f.lines.map((l, idx) => idx === i ? { ...l, [field]: value } : l),
    }));
  };
  const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, emptyLine()] }));
  const removeLine = (i) => setForm((f) => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));

  const submit = async (e) => {
    e.preventDefault();
    if (!totals.balanced) return toast.error("El asiento no balancea");
    if (!form.description.trim()) return toast.error("Descripción obligatoria");
    if (form.lines.some((l) => !l.account_code)) return toast.error("Cada línea requiere una cuenta");
    setSaving(true);
    try {
      await api.post("/accounting/journal", {
        date: form.date, description: form.description, reference: form.reference,
        lines: form.lines.map((l) => ({
          account_code: l.account_code,
          debit: Number(l.debit || 0),
          credit: Number(l.credit || 0),
          description: l.description || "",
        })),
      });
      toast.success("Asiento creado");
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Nuevo asiento de diario</DialogTitle>
          <DialogDescription>La suma de débitos debe ser igual a la suma de créditos.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Fecha</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1" data-testid="je-date" /></div>
            <div><Label>Referencia</Label><Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="mt-1" placeholder="Cheque #, factura, etc." data-testid="je-ref" /></div>
            <div><Label>Descripción *</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required className="mt-1" data-testid="je-desc" /></div>
          </div>
          <div className="border border-border rounded-md">
            <table className="w-full text-sm">
              <thead className="overline border-b border-border">
                <tr>
                  <th className="text-left p-2">Cuenta</th>
                  <th className="text-left p-2">Descripción</th>
                  <th className="text-right p-2">Débito</th>
                  <th className="text-right p-2">Crédito</th>
                  <th className="p-2"></th>
                </tr>
              </thead>
              <tbody>
                {form.lines.map((l, i) => (
                  <tr key={i} className="border-b border-border/40">
                    <td className="p-1.5">
                      <Select value={l.account_code} onValueChange={(v) => updateLine(i, "account_code", v)}>
                        <SelectTrigger className="h-8 text-xs" data-testid={`je-line-${i}-acc`}><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          {accounts.map((a) => <SelectItem key={a.code} value={a.code}>{a.code} · {a.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="p-1.5"><Input value={l.description || ""} onChange={(e) => updateLine(i, "description", e.target.value)} className="h-8 text-xs" placeholder="Concepto" /></td>
                    <td className="p-1.5"><Input type="number" step="0.01" min="0" value={l.debit || ""} onChange={(e) => updateLine(i, "debit", e.target.value)} className="h-8 text-right data-cell" data-testid={`je-line-${i}-dr`} /></td>
                    <td className="p-1.5"><Input type="number" step="0.01" min="0" value={l.credit || ""} onChange={(e) => updateLine(i, "credit", e.target.value)} className="h-8 text-right data-cell" data-testid={`je-line-${i}-cr`} /></td>
                    <td className="p-1.5">
                      {form.lines.length > 2 && <Button type="button" size="sm" variant="ghost" onClick={() => removeLine(i)}><X className="w-4 h-4" /></Button>}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t border-border">
                <tr className="font-bold">
                  <td colSpan={2} className="p-2 text-right">Totales:</td>
                  <td className="p-2 text-right data-cell">{money(totals.td)}</td>
                  <td className="p-2 text-right data-cell">{money(totals.tc)}</td>
                  <td className="p-2 text-center">
                    {totals.balanced ? <CheckCircle2 className="w-4 h-4 text-emerald-600 inline" data-testid="je-balanced" /> : <AlertCircle className="w-4 h-4 text-destructive inline" data-testid="je-unbalanced" />}
                  </td>
                </tr>
              </tfoot>
            </table>
            <div className="p-2 border-t border-border">
              <Button type="button" size="sm" variant="outline" onClick={addLine} data-testid="je-add-line"><Plus className="w-4 h-4 mr-1" /> Agregar línea</Button>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={saving || !totals.balanced} className="bg-primary font-bold" data-testid="je-save">Registrar asiento</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function emptyLine() { return { account_code: "", description: "", debit: "", credit: "" }; }

function JournalDetailDialog({ entry, onClose }) {
  if (!entry) return null;
  return (
    <Dialog open={!!entry} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Asiento {entry.number}</DialogTitle>
          <DialogDescription>{entry.description} · {(entry.date || "").slice(0, 10)}{entry.reference ? ` · Ref: ${entry.reference}` : ""}</DialogDescription>
        </DialogHeader>
        <table className="w-full text-sm border border-border">
          <thead className="overline border-b border-border">
            <tr><th className="text-left p-2">Cuenta</th><th className="text-left p-2">Detalle</th><th className="text-right p-2">Débito</th><th className="text-right p-2">Crédito</th></tr>
          </thead>
          <tbody>
            {entry.lines.map((l, i) => (
              <tr key={i} className="border-b border-border/40">
                <td className="p-2"><b>{l.account_code}</b> · {l.account_name}</td>
                <td className="p-2 text-xs">{l.description || ""}</td>
                <td className="p-2 text-right data-cell">{l.debit ? money(l.debit) : "—"}</td>
                <td className="p-2 text-right data-cell">{l.credit ? money(l.credit) : "—"}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="font-bold border-t border-border">
            <tr>
              <td colSpan={2} className="p-2 text-right">Totales:</td>
              <td className="p-2 text-right data-cell">{money(entry.total_debit)}</td>
              <td className="p-2 text-right data-cell">{money(entry.total_credit)}</td>
            </tr>
          </tfoot>
        </table>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────── TRIAL BALANCE ───────────────────────────────
export function TrialBalanceTab() {
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [includeZero, setIncludeZero] = useState(false);
  const [data, setData] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/accounting/reports/trial-balance", {
        params: { date_from: dateFrom || undefined, date_to: dateTo || undefined, include_zero: includeZero },
      });
      setData(data);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [dateFrom, dateTo, includeZero]);

  const downloadPdf = async () => {
    try {
      const { data } = await api.get("/accounting/reports/trial-balance.pdf", {
        params: { date_from: dateFrom || undefined, date_to: dateTo || undefined, include_zero: includeZero },
        responseType: "blob",
      });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = `balance_comprobacion_${new Date().toISOString().slice(0,10)}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (!data) return <div className="text-center p-8 text-muted-foreground">Cargando…</div>;
  const t = data.totals;

  return (
    <div>
      <div className="flex flex-wrap gap-2 items-end mb-3">
        <div>
          <Label className="text-xs">Desde</Label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-8 w-40" data-testid="tb-date-from" />
        </div>
        <div>
          <Label className="text-xs">Hasta</Label>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-8 w-40" data-testid="tb-date-to" />
        </div>
        <label className="flex items-center gap-2 text-xs mb-1.5">
          <input type="checkbox" checked={includeZero} onChange={(e) => setIncludeZero(e.target.checked)} data-testid="tb-include-zero" />
          Incluir cuentas con saldo cero
        </label>
        <div className={`ml-auto flex items-center gap-2 text-xs font-bold ${data.balances_ok ? "text-emerald-700 dark:text-emerald-400" : "text-destructive"}`} data-testid="tb-balance-status">
          {data.balances_ok ? <><CheckCircle2 className="w-4 h-4" /> Débitos = Créditos</> : <><AlertCircle className="w-4 h-4" /> DESBALANCE</>}
        </div>
        <Button variant="outline" size="sm" onClick={downloadPdf} data-testid="tb-print"><Printer className="w-4 h-4 mr-1.5" /> Imprimir</Button>
      </div>

      <div className="bg-card border border-border rounded-md overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="overline border-b border-border">
            <tr>
              <th className="text-left p-2">Código</th>
              <th className="text-left p-2">Cuenta</th>
              <th className="text-right p-2">Saldo Inicial<br/>Deudor</th>
              <th className="text-right p-2">Saldo Inicial<br/>Acreedor</th>
              <th className="text-right p-2">Débitos<br/>del período</th>
              <th className="text-right p-2">Créditos<br/>del período</th>
              <th className="text-right p-2">Saldo Final<br/>Deudor</th>
              <th className="text-right p-2">Saldo Final<br/>Acreedor</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.code} className="border-b border-border/40 hover:bg-accent" data-testid={`tb-row-${r.code}`}>
                <td className="p-2 data-cell font-bold">{r.code}</td>
                <td className="p-2">{r.name}</td>
                <td className="p-2 text-right data-cell">{r.opening_debit ? money(r.opening_debit) : "—"}</td>
                <td className="p-2 text-right data-cell">{r.opening_credit ? money(r.opening_credit) : "—"}</td>
                <td className="p-2 text-right data-cell">{r.period_debit ? money(r.period_debit) : "—"}</td>
                <td className="p-2 text-right data-cell">{r.period_credit ? money(r.period_credit) : "—"}</td>
                <td className="p-2 text-right data-cell font-bold">{r.ending_debit ? money(r.ending_debit) : "—"}</td>
                <td className="p-2 text-right data-cell font-bold">{r.ending_credit ? money(r.ending_credit) : "—"}</td>
              </tr>
            ))}
            {data.rows.length === 0 && (
              <tr><td colSpan={8} className="p-8 text-center text-muted-foreground">Sin movimientos en el período.</td></tr>
            )}
          </tbody>
          <tfoot className="font-bold border-t border-border">
            <tr className="bg-muted">
              <td colSpan={2} className="p-2 text-right">TOTALES</td>
              <td className="p-2 text-right data-cell">{money(t.opening_debit)}</td>
              <td className="p-2 text-right data-cell">{money(t.opening_credit)}</td>
              <td className="p-2 text-right data-cell">{money(t.period_debit)}</td>
              <td className="p-2 text-right data-cell">{money(t.period_credit)}</td>
              <td className="p-2 text-right data-cell">{money(t.ending_debit)}</td>
              <td className="p-2 text-right data-cell">{money(t.ending_credit)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}


// ─────────────────────────────── LIBRO MAYOR ───────────────────────────────
export function LedgerTab() {
  const [accounts, setAccounts] = useState([]);
  const [accountCode, setAccountCode] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/accounting/coa", { params: { active_only: true } })
      .then(({ data }) => setAccounts(data.filter((a) => !a.is_header)))
      .catch((err) => toast.error(formatApiError(err)));
  }, []);

  const load = async () => {
    if (!accountCode) return;
    setLoading(true);
    try {
      const { data } = await api.get("/accounting/reports/general-ledger", {
        params: { account_code: accountCode, date_from: dateFrom || undefined, date_to: dateTo || undefined },
      });
      setData(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (accountCode) load(); /* eslint-disable-next-line */ }, [accountCode, dateFrom, dateTo]);

  return (
    <div>
      <div className="flex flex-wrap gap-2 items-end mb-3">
        <div className="min-w-[280px]">
          <Label className="text-xs">Cuenta</Label>
          <Select value={accountCode} onValueChange={setAccountCode}>
            <SelectTrigger className="mt-1 h-9" data-testid="ledger-account"><SelectValue placeholder="Selecciona una cuenta…" /></SelectTrigger>
            <SelectContent>
              {accounts.map((a) => <SelectItem key={a.code} value={a.code}>{a.code} · {a.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Desde</Label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 w-40" data-testid="ledger-date-from" />
        </div>
        <div>
          <Label className="text-xs">Hasta</Label>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 w-40" data-testid="ledger-date-to" />
        </div>
      </div>

      {!accountCode && <div className="p-8 text-center text-muted-foreground">Selecciona una cuenta para ver su libro mayor.</div>}
      {accountCode && loading && <div className="p-8 text-center text-muted-foreground">Cargando…</div>}
      {accountCode && !loading && data && (
        <div className="bg-card border border-border rounded-md overflow-x-auto">
          <div className="p-3 border-b border-border flex items-center gap-4">
            <div>
              <div className="overline">Cuenta</div>
              <div className="font-bold">{data.account.code} · {data.account.name}</div>
            </div>
            <div className="ml-auto text-right">
              <div className="overline">Saldo inicial</div>
              <div className="text-lg font-bold data-cell">{money(data.opening_balance)}</div>
            </div>
            <div className="text-right">
              <div className="overline">Saldo final</div>
              <div className="text-2xl font-black data-cell text-primary" data-testid="ledger-ending">{money(data.ending_balance)}</div>
            </div>
          </div>
          <table className="w-full text-sm">
            <thead className="overline border-b border-border">
              <tr>
                <th className="text-left p-2">Fecha</th>
                <th className="text-left p-2">Asiento</th>
                <th className="text-left p-2">Descripción</th>
                <th className="text-left p-2">Referencia</th>
                <th className="text-right p-2">Débito</th>
                <th className="text-right p-2">Crédito</th>
                <th className="text-right p-2">Saldo</th>
              </tr>
            </thead>
            <tbody>
              {data.movements.map((m, i) => (
                <tr key={i} className="border-b border-border/40 hover:bg-accent" data-testid={`ledger-mov-${i}`}>
                  <td className="p-2 data-cell text-xs">{m.date}</td>
                  <td className="p-2 data-cell font-bold text-xs">{m.entry_number}</td>
                  <td className="p-2">{m.description}</td>
                  <td className="p-2 text-xs data-cell text-muted-foreground">{m.reference || "—"}</td>
                  <td className="p-2 text-right data-cell">{m.debit ? money(m.debit) : "—"}</td>
                  <td className="p-2 text-right data-cell">{m.credit ? money(m.credit) : "—"}</td>
                  <td className="p-2 text-right data-cell font-bold">{money(m.balance)}</td>
                </tr>
              ))}
              {data.movements.length === 0 && (
                <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">Sin movimientos en el período.</td></tr>
              )}
            </tbody>
            <tfoot className="font-bold border-t border-border">
              <tr className="bg-muted">
                <td colSpan={4} className="p-2 text-right">TOTALES</td>
                <td className="p-2 text-right data-cell">{money(data.totals.debit)}</td>
                <td className="p-2 text-right data-cell">{money(data.totals.credit)}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────── ESTADO DE RESULTADOS ───────────────────────────────
function IsRow({ r, indent = 0 }) {
  return (
    <tr className="border-b border-border/40">
      <td className="p-2 data-cell text-xs" style={{ paddingLeft: `${8 + indent * 12}px` }}>{r.code}</td>
      <td className="p-2">{r.name}</td>
      <td className="p-2 text-right data-cell">{money(r.balance)}</td>
    </tr>
  );
}
function IsTotal({ label, value, kind = "normal", testid }) {
  const cls = kind === "primary" ? "text-primary text-lg" : kind === "success" ? "text-success text-2xl" : "";
  return (
    <tr className={`border-t border-border font-bold bg-muted/50 ${kind === "success" ? "border-t-2 border-primary" : ""}`}>
      <td colSpan={2} className="p-2 text-right">{label}</td>
      <td className={`p-2 text-right data-cell ${cls}`} data-testid={testid}>{money(value)}</td>
    </tr>
  );
}

export function IncomeStatementTab() {
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [data, setData] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/accounting/reports/income-statement", {
        params: { date_from: dateFrom || undefined, date_to: dateTo || undefined },
      });
      setData(data);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [dateFrom, dateTo]);

  if (!data) return <div className="text-center p-8 text-muted-foreground">Cargando…</div>;
  const t = data.totals;

  return (
    <div>
      <div className="flex flex-wrap gap-2 items-end mb-3">
        <div>
          <Label className="text-xs">Desde</Label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 w-40" data-testid="is-date-from" />
        </div>
        <div>
          <Label className="text-xs">Hasta</Label>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 w-40" data-testid="is-date-to" />
        </div>
        <div className="ml-auto text-sm">
          <span className="text-muted-foreground">Utilidad neta:</span>{" "}
          <span className={`font-black text-lg data-cell ${t.utilidad_neta >= 0 ? "text-success" : "text-destructive"}`} data-testid="is-net-income">
            {money(t.utilidad_neta)}
          </span>
        </div>
      </div>

      <div className="bg-card border border-border rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="overline border-b border-border">
            <tr>
              <th className="text-left p-2 w-24">Código</th>
              <th className="text-left p-2">Cuenta</th>
              <th className="text-right p-2 w-40">Monto</th>
            </tr>
          </thead>
          <tbody>
            <tr className="bg-primary/5"><td colSpan={3} className="p-2 font-bold uppercase text-xs">Ingresos</td></tr>
            {data.ingresos.map((r) => <IsRow key={r.code} r={r} indent={1} />)}
            {data.ingresos.length === 0 && <tr><td colSpan={3} className="p-2 text-center text-muted-foreground text-xs">Sin ingresos en el período</td></tr>}
            <IsTotal label="Total ingresos" value={t.total_ingresos} />
            {data.contra_ingresos.length > 0 && (
              <>
                <tr className="bg-primary/5"><td colSpan={3} className="p-2 font-bold uppercase text-xs">(-) Devoluciones y descuentos</td></tr>
                {data.contra_ingresos.map((r) => <IsRow key={r.code} r={r} indent={1} />)}
                <IsTotal label="(-) Total devoluciones/descuentos" value={t.total_contra_ingresos} />
              </>
            )}
            <IsTotal label="Ingresos netos" value={t.ingresos_netos} kind="primary" />
            <tr className="bg-primary/5"><td colSpan={3} className="p-2 font-bold uppercase text-xs">(-) Costos</td></tr>
            {data.costos.map((r) => <IsRow key={r.code} r={r} indent={1} />)}
            {data.costos.length === 0 && <tr><td colSpan={3} className="p-2 text-center text-muted-foreground text-xs">Sin costos en el período</td></tr>}
            <IsTotal label="Total costos" value={t.total_costos} />
            <IsTotal label="Utilidad bruta" value={t.utilidad_bruta} kind="primary" />
            <tr className="bg-primary/5"><td colSpan={3} className="p-2 font-bold uppercase text-xs">(-) Gastos operativos y financieros</td></tr>
            {data.gastos.map((r) => <IsRow key={r.code} r={r} indent={1} />)}
            {data.gastos.length === 0 && <tr><td colSpan={3} className="p-2 text-center text-muted-foreground text-xs">Sin gastos en el período</td></tr>}
            <IsTotal label="Total gastos" value={t.total_gastos} />
            <IsTotal label="UTILIDAD NETA DEL PERÍODO" value={t.utilidad_neta} kind="success" testid="is-final-net" />
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─────────────────────────────── BALANCE GENERAL ───────────────────────────────
function BsSection({ title, items, total, totalLabel, testid }) {
  return (
    <div className="bg-card border border-border rounded-md overflow-hidden">
      <div className="px-3 py-2 border-b border-border bg-primary/5 font-bold uppercase text-xs">{title}</div>
      <table className="w-full text-sm">
        <tbody>
          {items.map((r) => (
            <tr key={r.code} className="border-b border-border/40">
              <td className="p-2 data-cell text-xs w-20">{r.code}</td>
              <td className="p-2">{r.name}</td>
              <td className="p-2 text-right data-cell">{money(r.balance)}</td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan={3} className="p-4 text-center text-muted-foreground text-xs">Sin saldos</td></tr>}
        </tbody>
        <tfoot className="font-bold bg-muted">
          <tr className="border-t-2 border-primary">
            <td colSpan={2} className="p-2 text-right">{totalLabel}</td>
            <td className="p-2 text-right data-cell text-primary text-lg" data-testid={testid}>{money(total)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

export function BalanceSheetTab() {
  const [dateTo, setDateTo] = useState("");
  const [data, setData] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/accounting/reports/balance-sheet", {
        params: { date_to: dateTo || undefined },
      });
      setData(data);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [dateTo]);

  if (!data) return <div className="text-center p-8 text-muted-foreground">Cargando…</div>;
  const t = data.totals;

  return (
    <div>
      <div className="flex flex-wrap gap-2 items-end mb-3">
        <div>
          <Label className="text-xs">Fecha de corte</Label>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 w-40" data-testid="bs-date-to" />
        </div>
        <div className={`ml-auto flex items-center gap-2 text-xs font-bold ${data.cuadra ? "text-emerald-700 dark:text-emerald-400" : "text-destructive"}`} data-testid="bs-cuadra">
          {data.cuadra
            ? <><CheckCircle2 className="w-4 h-4" /> Activos = Pasivos + Patrimonio</>
            : <><AlertCircle className="w-4 h-4" /> DESBALANCE</>}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <BsSection
          title="Activos"
          items={data.activos}
          total={t.total_activos}
          totalLabel="TOTAL ACTIVOS"
          testid="bs-total-activos"
        />
        <div className="space-y-4">
          <BsSection
            title="Pasivos"
            items={data.pasivos}
            total={t.total_pasivos}
            totalLabel="TOTAL PASIVOS"
            testid="bs-total-pasivos"
          />
          <div className="bg-card border border-border rounded-md overflow-hidden">
            <div className="px-3 py-2 border-b border-border bg-primary/5 font-bold uppercase text-xs">Patrimonio</div>
            <table className="w-full text-sm">
              <tbody>
                {data.patrimonio.map((r) => (
                  <tr key={r.code} className="border-b border-border/40">
                    <td className="p-2 data-cell text-xs w-20">{r.code}</td>
                    <td className="p-2">{r.name}</td>
                    <td className="p-2 text-right data-cell">{money(r.balance)}</td>
                  </tr>
                ))}
                <tr className="border-b border-border/40 bg-muted/30">
                  <td className="p-2 data-cell text-xs w-20 italic">—</td>
                  <td className="p-2 italic">Utilidad del período (calculada)</td>
                  <td className={`p-2 text-right data-cell font-bold ${data.utilidad_periodo >= 0 ? "text-success" : "text-destructive"}`}>{money(data.utilidad_periodo)}</td>
                </tr>
              </tbody>
              <tfoot className="font-bold bg-muted">
                <tr className="border-t-2 border-primary">
                  <td colSpan={2} className="p-2 text-right">TOTAL PATRIMONIO</td>
                  <td className="p-2 text-right data-cell text-primary text-lg" data-testid="bs-total-patrimonio">{money(t.total_patrimonio)}</td>
                </tr>
                <tr className="border-t border-primary">
                  <td colSpan={2} className="p-2 text-right">TOTAL PASIVO + PATRIMONIO</td>
                  <td className="p-2 text-right data-cell text-primary text-lg" data-testid="bs-total-pas-pat">{money(t.total_pasivo_patrimonio)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}


// ─────────────────────────────── CIERRE DE PERIODO ───────────────────────────────
export function PeriodCloseTab() {
  const [periods, setPeriods] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [form, setForm] = useState({ period: "", notes: "" });
  const [reopenDialog, setReopenDialog] = useState(null);
  const [reopenReason, setReopenReason] = useState("");
  const [saving, setSaving] = useState(false);
  const { user } = useAuth();
  const isAdmin = (user?.role || "").toLowerCase() === "admin";

  const load = async () => {
    try {
      const { data } = await api.get("/accounting/periods");
      setPeriods(data);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, []);

  const defaultPrevMonth = () => {
    const d = new Date();
    d.setDate(1); d.setMonth(d.getMonth() - 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  };

  const openClose = () => {
    setForm({ period: defaultPrevMonth(), notes: "" });
    setOpenDialog(true);
  };

  const submitClose = async (e) => {
    e.preventDefault();
    if (!form.period.match(/^\d{4}-\d{2}$/)) return toast.error("Formato inválido. Usa YYYY-MM.");
    setSaving(true);
    try {
      await api.post("/accounting/periods/close", form);
      toast.success(`Período ${form.period} cerrado exitosamente`);
      setOpenDialog(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const submitReopen = async (e) => {
    e.preventDefault();
    if (!reopenReason.trim()) return toast.error("Debes indicar la razón");
    setSaving(true);
    try {
      await api.post("/accounting/periods/reopen", { period: reopenDialog.period, reason: reopenReason });
      toast.success(`Período ${reopenDialog.period} reabierto`);
      setReopenDialog(null);
      setReopenReason("");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const downloadPdf = async (period) => {
    try {
      const { data } = await api.get(`/accounting/periods/${period}/report.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = `cierre_${period}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <div>
          <p className="text-sm text-muted-foreground">
            Al cerrar un período, no se podrán registrar ni anular asientos con fecha dentro de ese mes.
            Se guarda un snapshot de los reportes financieros (Estado de Resultados, Balance General, Balance de Comprobación).
          </p>
        </div>
        {isAdmin && (
          <Button onClick={openClose} className="bg-primary font-bold ml-auto" data-testid="close-period-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Cerrar período
          </Button>
        )}
      </div>

      <div className="bg-card border border-border rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="overline border-b border-border">
            <tr>
              <th className="text-left p-3">Período</th>
              <th className="text-left p-3">Estado</th>
              <th className="text-left p-3">Cerrado por</th>
              <th className="text-left p-3">Fecha cierre</th>
              <th className="text-right p-3">Utilidad neta</th>
              <th className="text-right p-3">Total activos</th>
              <th className="text-right p-3">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {periods.length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">Sin períodos cerrados aún.</td></tr>
            )}
            {periods.map((p) => (
              <tr key={p.period} className="border-b border-border/60 hover:bg-accent" data-testid={`period-row-${p.period}`}>
                <td className="p-3 data-cell font-bold">{p.period}</td>
                <td className="p-3">
                  {p.status === "closed"
                    ? <span className="text-xs px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-700 dark:text-emerald-400">CERRADO</span>
                    : <span className="text-xs px-2 py-0.5 rounded bg-amber-500/10 text-amber-700 dark:text-amber-400">REABIERTO</span>}
                </td>
                <td className="p-3 text-xs">{p.closed_by || "—"}</td>
                <td className="p-3 data-cell text-xs">{(p.closed_at || "").slice(0, 10)}</td>
                <td className="p-3 text-right data-cell font-bold">{money(p.totals?.utilidad_neta || 0)}</td>
                <td className="p-3 text-right data-cell">{money(p.totals?.total_activos || 0)}</td>
                <td className="p-3 text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" onClick={() => downloadPdf(p.period)} data-testid={`pdf-${p.period}`}>
                    <Printer className="w-4 h-4" />
                  </Button>
                  {isAdmin && p.status === "closed" && (
                    <Button size="sm" variant="ghost" onClick={() => setReopenDialog(p)} data-testid={`reopen-${p.period}`}>
                      <RotateCcw className="w-4 h-4 text-destructive" />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Dialog Cerrar */}
      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cerrar período contable</DialogTitle>
            <DialogDescription>
              El cierre bloquea nuevos asientos en ese mes. Requiere que débitos = créditos en el período.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitClose} className="space-y-3">
            <div>
              <Label>Período (YYYY-MM) *</Label>
              <Input value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} placeholder="2026-01" className="mt-1 data-cell" data-testid="close-period-input" required />
            </div>
            <div>
              <Label>Notas (opcional)</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="mt-1" data-testid="close-notes-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpenDialog(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} className="bg-primary font-bold" data-testid="close-confirm">Cerrar período</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Reabrir */}
      <Dialog open={!!reopenDialog} onOpenChange={() => setReopenDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reabrir período {reopenDialog?.period}</DialogTitle>
            <DialogDescription>
              Solo puedes reabrir el período cerrado más reciente. Se registrará la razón para auditoría.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitReopen} className="space-y-3">
            <div>
              <Label>Razón de la reapertura *</Label>
              <Textarea value={reopenReason} onChange={(e) => setReopenReason(e.target.value)} rows={3} className="mt-1" data-testid="reopen-reason-input" required />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setReopenDialog(null)}>Cancelar</Button>
              <Button type="submit" disabled={saving} variant="destructive" data-testid="reopen-confirm">Reabrir</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─────────────────────────────── CIERRE ANUAL ───────────────────────────────
export function YearEndTab() {
  const [closes, setCloses] = useState([]);
  const [previewYear, setPreviewYear] = useState("");
  const [preview, setPreview] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [form, setForm] = useState({ year: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const { user } = useAuth();
  const isAdmin = (user?.role || "").toLowerCase() === "admin";

  const load = async () => {
    try {
      const { data } = await api.get("/accounting/year-end");
      setCloses(data);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, []);

  const loadPreview = async (year) => {
    if (!year || !year.match(/^\d{4}$/)) {
      setPreview(null);
      return;
    }
    try {
      const { data } = await api.get(`/accounting/year-end/${year}/preview`);
      setPreview(data);
    } catch (err) {
      setPreview(null);
      toast.error(formatApiError(err));
    }
  };

  const openClose = () => {
    const prevYear = new Date().getFullYear() - 1;
    setForm({ year: String(prevYear), notes: "" });
    setPreviewYear(String(prevYear));
    loadPreview(String(prevYear));
    setOpenDialog(true);
  };

  const submitClose = async (e) => {
    e.preventDefault();
    const year = parseInt(form.year, 10);
    if (isNaN(year)) return toast.error("Año inválido");
    setSaving(true);
    try {
      await api.post("/accounting/year-end/close", { year, notes: form.notes });
      toast.success(`Cierre anual ${year} completado`);
      setOpenDialog(false);
      setPreview(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <p className="text-sm text-muted-foreground max-w-2xl">
          Cancela las cuentas de resultado (Ingresos, Costos, Gastos) contra <b>3301 Utilidad del Ejercicio</b>,
          y luego traslada ese saldo a <b>3201 Utilidades Retenidas</b>. Requiere que los 12 meses del año estén cerrados.
        </p>
        {isAdmin && (
          <Button onClick={openClose} className="bg-primary font-bold ml-auto" data-testid="year-end-close-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Cerrar año
          </Button>
        )}
      </div>

      <div className="bg-card border border-border rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="overline border-b border-border">
            <tr>
              <th className="text-left p-3">Año</th>
              <th className="text-left p-3">Cerrado por</th>
              <th className="text-left p-3">Fecha</th>
              <th className="text-right p-3">Ingresos</th>
              <th className="text-right p-3">Gastos + Costos</th>
              <th className="text-right p-3">Utilidad neta</th>
              <th className="text-left p-3">Asientos</th>
            </tr>
          </thead>
          <tbody>
            {closes.length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">Sin cierres anuales aún.</td></tr>
            )}
            {closes.map((c) => (
              <tr key={c.year} className="border-b border-border/60 hover:bg-accent" data-testid={`year-close-${c.year}`}>
                <td className="p-3 data-cell font-bold text-lg">{c.year}</td>
                <td className="p-3 text-xs">{c.closed_by}</td>
                <td className="p-3 data-cell text-xs">{(c.closed_at || "").slice(0, 10)}</td>
                <td className="p-3 text-right data-cell">{money(c.total_ingresos)}</td>
                <td className="p-3 text-right data-cell">{money(c.total_gastos_costos)}</td>
                <td className={`p-3 text-right data-cell font-bold ${c.utilidad_neta >= 0 ? "text-success" : "text-destructive"}`}>{money(c.utilidad_neta)}</td>
                <td className="p-3 text-xs">
                  {c.close_entry_number && <div>Cierre: <b>{c.close_entry_number}</b></div>}
                  {c.transfer_entry_number && <div>Traslado: <b>{c.transfer_entry_number}</b></div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Cerrar año fiscal</DialogTitle>
            <DialogDescription>
              Se generarán 2 asientos: (1) cancelación de cuentas de resultado y (2) traslado a Utilidades Retenidas (3201).
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitClose} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Año *</Label>
                <Input
                  type="number"
                  min="2020" max="2099"
                  value={form.year}
                  onChange={(e) => {
                    setForm({ ...form, year: e.target.value });
                    setPreviewYear(e.target.value);
                    loadPreview(e.target.value);
                  }}
                  className="mt-1 data-cell"
                  data-testid="year-input"
                  required
                />
              </div>
              <div>
                <Label>Notas (opcional)</Label>
                <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1" data-testid="year-notes-input" />
              </div>
            </div>
            {preview && (
              <div className="border border-border rounded-md p-3 bg-muted/30 text-sm" data-testid="year-preview">
                <div className="font-bold mb-2">Vista previa del cierre {preview.year}</div>
                <div className="grid grid-cols-3 gap-2 mb-2">
                  <div><span className="text-muted-foreground text-xs">Ingresos netos:</span> <b className="text-success">{money(preview.totals.ingresos_netos)}</b></div>
                  <div><span className="text-muted-foreground text-xs">Costos + Gastos:</span> <b>{money(preview.totals.total_costos + preview.totals.total_gastos)}</b></div>
                  <div><span className="text-muted-foreground text-xs">Utilidad neta:</span> <b className={preview.totals.utilidad_neta >= 0 ? "text-success" : "text-destructive"}>{money(preview.totals.utilidad_neta)}</b></div>
                </div>
                <div className="text-xs text-muted-foreground">
                  {preview.lines_preview.length} cuentas de resultado serán canceladas. La utilidad/pérdida se trasladará a la cuenta {preview.will_transfer_to}.
                </div>
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpenDialog(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} className="bg-primary font-bold" data-testid="year-close-confirm">Ejecutar cierre anual</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

